import { Link } from "react-router-dom";
import { Phone, Mail } from "lucide-react";

const SHOP_PHONE = "(877) 400-0889";
const SHOP_EMAIL = "brett@screenzz.com";

/**
 * Shared chrome for /privacy-policy and /terms.
 *
 * Keeps legal pages visually consistent with the rest of order.screenzz.com
 * (same header logo + footer), but uses a wider, prose-friendly column so
 * the content is easy to read and skim. The footer includes all three
 * required compliance links so Twilio verification reviewers can always
 * confirm cross-linking.
 */
export default function LegalPageLayout({ title, subtitle, lastUpdated, children }) {
  return (
    <div className="min-h-screen flex flex-col" style={{ background: "#f7faf6" }}>
      <header className="px-5 py-4 border-b bg-white sticky top-0 z-20">
        <div className="max-w-3xl mx-auto flex items-center justify-between">
          <Link to="/" data-testid="legal-logo-link" className="flex items-center gap-2">
            <img
              src="https://customer-assets.emergentagent.com/job_tech-dispatch-31/artifacts/a6ta9f48_SCREENZZ-LOGO-RGB_TextOnly.png"
              alt="ScreenzZ"
              className="h-8"
            />
          </Link>
          <Link
            to="/"
            data-testid="legal-back-home"
            className="text-xs font-semibold muted-text hover:brand-text underline-offset-2 hover:underline"
          >
            Back to home →
          </Link>
        </div>
      </header>

      <main className="flex-1 px-5 py-8 sm:py-12">
        <div className="max-w-3xl mx-auto">
          <p className="text-xs font-bold uppercase tracking-wider brand-text mb-2">Legal</p>
          <h1 className="text-3xl sm:text-4xl font-extrabold tracking-tight text-[var(--brand-text)]">
            {title}
          </h1>
          {subtitle && <p className="mt-3 muted-text leading-relaxed">{subtitle}</p>}
          {lastUpdated && (
            <p className="mt-2 text-xs muted-text" data-testid="legal-last-updated">
              Last updated: {lastUpdated}
            </p>
          )}
          <div className="prose prose-sm sm:prose-base max-w-none mt-8 text-[var(--brand-text)] leading-relaxed">
            {children}
          </div>

          <div className="mt-12 rounded-2xl border-2 border-[var(--brand-border)] bg-white p-5">
            <p className="text-xs font-bold uppercase tracking-wider brand-text mb-2">Contact ScreenzZ</p>
            <p className="font-extrabold text-[var(--brand-text)]">ScreenzZ Window Screen Repair</p>
            <div className="mt-2 space-y-1 text-sm">
              <p className="flex items-center gap-2 muted-text">
                <Phone className="w-4 h-4 brand-text" />
                <a
                  href={`tel:${SHOP_PHONE.replace(/[^0-9]/g, "")}`}
                  className="brand-text font-semibold hover:underline"
                  data-testid="legal-contact-phone"
                >
                  {SHOP_PHONE}
                </a>
              </p>
              <p className="flex items-center gap-2 muted-text">
                <Mail className="w-4 h-4 brand-text" />
                <a
                  href={`mailto:${SHOP_EMAIL}`}
                  className="brand-text font-semibold hover:underline"
                  data-testid="legal-contact-email"
                >
                  {SHOP_EMAIL}
                </a>
              </p>
            </div>
          </div>
        </div>
      </main>

      <footer className="border-t border-[var(--brand-border)] bg-white">
        <div className="max-w-3xl mx-auto px-5 py-6 flex flex-wrap items-center justify-between gap-3 text-xs muted-text">
          <p>© {new Date().getFullYear()} ScreenzZ · DIY Ordering</p>
          <div className="flex items-center gap-4">
            <Link
              to="/privacy-policy"
              data-testid="legal-footer-privacy"
              className="muted-text hover:brand-text underline-offset-2 hover:underline"
            >
              Privacy Policy
            </Link>
            <Link
              to="/terms"
              data-testid="legal-footer-terms"
              className="muted-text hover:brand-text underline-offset-2 hover:underline"
            >
              Terms &amp; Conditions
            </Link>
            <a
              href={`tel:${SHOP_PHONE.replace(/[^0-9]/g, "")}`}
              data-testid="legal-footer-contact"
              className="muted-text hover:brand-text underline-offset-2 hover:underline"
            >
              Contact ScreenzZ
            </a>
          </div>
        </div>
      </footer>
    </div>
  );
}
